// Utility functions for XML diffing and history descriptions

export function getXMLModelSets(xmlDoc: Document): { pedGroups: Set<string>; vehGroups: Set<string> } {
  const result = { pedGroups: new Set<string>(), vehGroups: new Set<string>() };

  (['pedGroups', 'vehGroups'] as const).forEach(groupType => {
    // Handle both Name and n elements
    const nameElements = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item > models > Item > Name`));
    const nElements = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item > models > Item > n`));

    const models = [...nameElements, ...nElements]
      .map(node => node.textContent || '')
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);

    result[groupType] = new Set(models);
  });

  return result;
}

export function getGroupModels(xmlDoc: Document, groupType: 'pedGroups' | 'vehGroups'): Map<number, { name: string; models: Set<string> }> {
  const groupModels = new Map<number, { name: string; models: Set<string> }>();
  const items = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item`));

  items.forEach((item, index) => {
    // Get group name from Name element
    const nameElement = item.querySelector('Name');
    const groupName = nameElement?.textContent || `group ${index}`;

    const nameElements = Array.from(item.querySelectorAll('models > Item > Name'));
    const nElements = Array.from(item.querySelectorAll('models > Item > n'));

    const models = [...nameElements, ...nElements]
      .map(node => node.textContent || '')
      .filter(Boolean);

    groupModels.set(index, { name: groupName, models: new Set(models) });
  });

  return groupModels;
}

export function compareXMLStates(oldDoc: Document | null, newDoc: Document): string {
  if (!oldDoc) {
    // First state
    const newPedModels = getGroupModels(newDoc, 'pedGroups');
    const newVehModels = getGroupModels(newDoc, 'vehGroups');
    const totalPeds = Array.from(newPedModels.values()).reduce((sum, group) => sum + group.models.size, 0);
    const totalVehs = Array.from(newVehModels.values()).reduce((sum, group) => sum + group.models.size, 0);
    return `Initial: ${totalPeds} peds, ${totalVehs} vehicles`;
  }

  const oldPedModels = getGroupModels(oldDoc, 'pedGroups');
  const newPedModels = getGroupModels(newDoc, 'pedGroups');
  const oldVehModels = getGroupModels(oldDoc, 'vehGroups');
  const newVehModels = getGroupModels(newDoc, 'vehGroups');

  const changes: string[] = [];

  // Check ped groups for changes
  const maxPedGroups = Math.max(oldPedModels.size, newPedModels.size);
  for (let i = 0; i < maxPedGroups; i++) {
    const oldGroup = oldPedModels.get(i);
    const newGroup = newPedModels.get(i);
    const oldSet = oldGroup?.models || new Set<string>();
    const newSet = newGroup?.models || new Set<string>();
    const groupName = newGroup?.name || oldGroup?.name || `group ${i}`;

    const added = Array.from(newSet).filter(model => !oldSet.has(model));
    const removed = Array.from(oldSet).filter(model => !newSet.has(model));

    if (added.length > 0) {
      const models = added.slice(0, 3).join(', ');
      const more = added.length > 3 ? ` +${added.length - 3}` : '';
      changes.push(`${models}${more} added to ${groupName}`);
    }
    if (removed.length > 0) {
      const models = removed.slice(0, 3).join(', ');
      const more = removed.length > 3 ? ` +${removed.length - 3}` : '';
      changes.push(`${models}${more} removed from ${groupName}`);
    }
  }

  // Check vehicle groups for changes
  const maxVehGroups = Math.max(oldVehModels.size, newVehModels.size);
  for (let i = 0; i < maxVehGroups; i++) {
    const oldGroup = oldVehModels.get(i);
    const newGroup = newVehModels.get(i);
    const oldSet = oldGroup?.models || new Set<string>();
    const newSet = newGroup?.models || new Set<string>();
    const groupName = newGroup?.name || oldGroup?.name || `group ${i}`;

    const added = Array.from(newSet).filter(model => !oldSet.has(model));
    const removed = Array.from(oldSet).filter(model => !newSet.has(model));

    if (added.length > 0) {
      const models = added.slice(0, 3).join(', ');
      const more = added.length > 3 ? ` +${added.length - 3}` : '';
      changes.push(`${models}${more} added to ${groupName}`);
    }
    if (removed.length > 0) {
      const models = removed.slice(0, 3).join(', ');
      const more = removed.length > 3 ? ` +${removed.length - 3}` : '';
      changes.push(`${models}${more} removed from ${groupName}`);
    }
  }

  if (changes.length === 0) {
    return '';
  }

  return changes.join(' | ');
}
